import { BehaviorSubject, Observable, ReplaySubject, Subject, filter, firstValueFrom, timer } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { ISessionInfo } from '@core/base/session-base.types';

export abstract class SessionBase
{
    public authState$: BehaviorSubject<boolean | undefined> = new BehaviorSubject<boolean | undefined>(undefined);
    protected readonly loginExchangeStartedSubject = new Subject<void>();
    public readonly loginExchangeStarted$ = this.loginExchangeStartedSubject.asObservable();

    protected sessionInfo!: ISessionInfo;
    protected _sessionInfo$: ReplaySubject<ISessionInfo> = new ReplaySubject<ISessionInfo>(1);

    private initState: boolean = false;
    private readonly BLANK_ID: string = '00000000-0000-0000-0000-000000000000';

    constructor()
    {
        this.init();
    }

    get sessionInfo$(): Observable<ISessionInfo>
    {
        return this._sessionInfo$.asObservable();
    }

    public getSession(): ISessionInfo
    {
        return this.sessionInfo;
    }

    public abstract check(): Promise<boolean>;

    public abstract getAccessToken(): string | null;

    public abstract login(userHint?: string): Promise<void>;

    public abstract logout(): Promise<unknown>;

    protected async reinit(): Promise<void>
    {
        this.initState = false;
        this.sessionInfo = undefined!;
        await this.init();
    }

    protected async waitInit(): Promise<void>
    {
        if (!this.initState)
        {
            await firstValueFrom(
                timer(0, 100).pipe(filter(() => this.initState))
            );
        }
    }

    protected async init()
    {
        if (this.initState) return;

        if (!this.sessionInfo)
        {
            // The saved pair is only for one-time legacy exchange. New sessions
            // keep an anonymous client ID in memory and never mint a device hash.
            const legacyDeviceHash = localStorage.getItem('device_hash');
            this.sessionInfo = {
                session_id: localStorage.getItem('session_id') ?? '',
                ...(legacyDeviceHash ? { device_hash: legacyDeviceHash } : {}),
            };

            if (!this.sessionInfo.session_id || this.sessionInfo.session_id == this.BLANK_ID)
            {
                this.sessionInfo.session_id = uuidv4();
            }
        }

        this._sessionInfo$.next(this.sessionInfo);
        this.initState = true;
    }
}
